import { Font } from '../types/Font';

const SVG_WIDTH = 800;
const SVG_HEIGHT = 200;
const MAX_NORMAL_FONT_SIZE = 72;
const MIN_FONT_SIZE = 10;
const TEXT_COLOR = '#2f2f2f';

const CURVED_VIEWBOX = '0 0 300 120';
const CURVED_PATH_D = 'M 20 80 Q 150 10 280 80';
const MAX_CURVED_FONT_SIZE = 48;
/** Approximate path length in SVG units (same path as FontCard) */
const CURVED_PATH_LENGTH = 320;
/** Assume wider chars + margin so nothing is clipped (0.55 was too optimistic). */
const CHAR_WIDTH_FACTOR = 0.8;
const SAFETY_MARGIN = 0.88;

/** Scale font size so long text fits the SVG width (no DOM measurement). */
function fontSizeForNormalText(textLength: number): number {
    if (textLength <= 0) return MAX_NORMAL_FONT_SIZE;
    const usableWidth = SVG_WIDTH * SAFETY_MARGIN;
    const estimated = Math.floor(usableWidth / (CHAR_WIDTH_FACTOR * textLength));
    return Math.max(MIN_FONT_SIZE, Math.min(MAX_NORMAL_FONT_SIZE, estimated));
}

/** Scale font size so long text fits the curved path (no DOM measurement). */
function fontSizeForCurvedText(textLength: number): number {
    if (textLength <= 0) return MAX_CURVED_FONT_SIZE;
    const usablePathLength = CURVED_PATH_LENGTH * SAFETY_MARGIN;
    const estimated = Math.floor(usablePathLength / (CHAR_WIDTH_FACTOR * textLength));
    return Math.max(MIN_FONT_SIZE, Math.min(MAX_CURVED_FONT_SIZE, estimated));
}

async function getFontAsBase64(fontUrl: string): Promise<{ base64: string; format: string }> {
    const response = await fetch(fontUrl, { mode: 'cors' });
    if (!response.ok) {
        throw new Error(`Failed to fetch font: ${response.statusText}`);
    }
    const blob = await response.blob();
    const format = blob.type || 'font/woff2';
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return { base64, format };
}

function getFontFaceCss(fontFamily: string, format: string, base64: string): string {
    const formatType = format.includes('woff2') ? 'woff2' : 'woff';
    return `@font-face { font-family: "${fontFamily}"; src: url("data:${format};base64,${base64}") format("${formatType}"); }`;
}

function escapeSvgText(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

async function buildNormalFontSvg(font: Font, previewText: string, textColor: string = TEXT_COLOR): Promise<string> {
    const { base64, format } = await getFontAsBase64(font.path);
    const fontFamily = font.name.replace(/"/g, "'");
    const fontFaceCss = getFontFaceCss(fontFamily, format, base64);
    const escapedText = escapeSvgText(previewText);
    const fontSize = fontSizeForNormalText(previewText.length);

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${SVG_WIDTH}" height="${SVG_HEIGHT}" viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}">
  <defs>
    <style>${fontFaceCss}</style>
  </defs>
  <text x="${SVG_WIDTH / 2}" y="${SVG_HEIGHT / 2 + fontSize / 3}" text-anchor="middle" dominant-baseline="middle" font-family="${fontFamily}" font-size="${fontSize}" font-weight="500" fill="${textColor}">${escapedText}</text>
</svg>`;
}

async function buildCurvedFontSvg(font: Font, previewText: string, textColor: string = TEXT_COLOR): Promise<string> {
    const { base64, format } = await getFontAsBase64(font.path);
    const fontFamily = font.name.replace(/"/g, "'");
    const fontFaceCss = getFontFaceCss(fontFamily, format, base64);
    const escapedText = escapeSvgText(previewText);
    const pathId = `curve-${font.id}`;
    const fontSize = fontSizeForCurvedText(previewText.length);

    return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="300" height="120" viewBox="${CURVED_VIEWBOX}">
  <defs>
    <style>${fontFaceCss}</style>
    <path id="${pathId}" d="${CURVED_PATH_D}"/>
  </defs>
  <text font-family="${fontFamily}" font-size="${fontSize}" font-weight="500" fill="${textColor}">
    <textPath xlink:href="#${pathId}" startOffset="50%" text-anchor="middle" dominant-baseline="middle">${escapedText}</textPath>
  </text>
</svg>`;
}

function svgStringToPngBlob(svgString: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || SVG_WIDTH;
            canvas.height = img.naturalHeight || SVG_HEIGHT;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas 2d context not available'));
                return;
            }
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
                (pngBlob) => {
                    if (pngBlob) {
                        resolve(pngBlob);
                    } else {
                        reject(new Error('Failed to create PNG blob'));
                    }
                },
                'image/png',
                1
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load SVG as image'));
        };

        img.src = url;
    });
}

export type FontStyle = 'normal' | 'curved';

export type FontSvgToPngOptions = {
    font: Font;
    previewText: string;
    style: FontStyle;
    textColor?: string;
};


export async function fontSvgToPng(options: FontSvgToPngOptions): Promise<Blob> {
    const { font, previewText, style, textColor = TEXT_COLOR } = options;
    const svgString =
        style === 'curved'
            ? await buildCurvedFontSvg(font, previewText, textColor)
            : await buildNormalFontSvg(font, previewText, textColor);
    return svgStringToPngBlob(svgString);
}
